import { runInAction, makeAutoObservable, reaction, when } from "mobx";
import { getDaysDiff, databaseDate } from "@intrasync/utils";
import { utils } from "@intrasync/utils";

import RootStore from "./RootStore";
import ServiceStore from "./ServiceStore";
import AIAgentStore from "../views/administration/AIAgent/AIAgentStore";

const appServerKey = ""; //Your VAPID Public Key goes here

interface AuthAddress {
  dev: string;
  stage: string;
  prod: string;
}

interface SetComponentDebugParams {
  nextComponentDomain: string;
  shouldTrace?: boolean;
}

const authAddress = (appName: string): Record<string, string> => {
  return {
    dev: `http://localhost:3072/auth/${appName}-token`,
    stage: `https://stage.intrasync.app/auth/${appName}-token`,
    prod: `https://millerprecast.intrasync.app/auth/${appName}-token`,
  };
};

// Enhanced breakpoint system following Material Design and modern responsive standards
const BREAKPOINTS = {
  xs: 0, // phones (portrait)
  sm: 600, // phones (landscape) / small tablets
  md: 960, // tablets / small laptops
  lg: 1280, // laptops / desktops
  xl: 1536, // large desktops
  xxl: 1920, // ultra-wide displays
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;

// Responsive layout constants with better scaling
const LAYOUT_CONSTANTS = {
  header: {
    height: {
      xs: 56, // Material Design mobile app bar
      sm: 56,
      md: 64, // Material Design desktop app bar
      lg: 66.8,
      xl: 66.8,
      xxl: 72,
    },
  },
  cardHeader: {
    height: {
      xs: 45,
      sm: 52,
      md: 60,
      lg: 67,
      xl: 67,
      xxl: 72,
    },
    padding: {
      horizontal: {
        xs: 16,
        sm: 24,
        md: 32,
        lg: 60,
        xl: 60,
        xxl: 80,
      },
    },
  },
  cardBody: {
    padding: {
      vertical: {
        xs: 8,
        sm: 16,
        md: 24,
        lg: 30,
        xl: 30,
        xxl: 40,
      },
      horizontal: {
        xs: 16,
        sm: 20,
        md: 24,
        lg: 32,
        xl: 32,
        xxl: 48,
      },
    },
  },
  sidebar: {
    mini: {
      xs: 0, // Hidden on mobile
      sm: 0, // Hidden on small tablets
      md: 72, // Material Design mini nav
      lg: 110,
      xl: 110,
      xxl: 120,
    },
    full: {
      xs: 280, // Full width overlay on mobile
      sm: 320, // Wider on small tablets
      md: 280,
      lg: 290,
      xl: 290,
      xxl: 320,
    },
  },
  table: {
    headerHeight: {
      xs: 100,
      sm: 110,
      md: 120,
      lg: 120,
      xl: 120,
      xxl: 140,
    },
    headerExtraHeight: {
      xs: 140,
      sm: 150,
      md: 160,
      lg: 170,
      xl: 170,
      xxl: 190,
    },
    padding: {
      xs: 16,
      sm: 24,
      md: 32,
      lg: 60,
      xl: 60,
      xxl: 80,
    },
  },
} as const;

// Note: Legacy constants removed as they are now calculated dynamically per breakpoint

const defaultSizes = {
  headerHeight: LAYOUT_CONSTANTS.header.height.lg,
  cardHeaderHeight: 200,
  tableHeaderHeight: LAYOUT_CONSTANTS.table.headerHeight.lg,
  tableHeaderExtraHeight: LAYOUT_CONSTANTS.table.headerExtraHeight.lg,
  tableWidthPadding: LAYOUT_CONSTANTS.table.padding.lg,
  miniSidebarWidth: LAYOUT_CONSTANTS.sidebar.mini.lg,
  fullSidebarWidth: LAYOUT_CONSTANTS.sidebar.full.lg,
};

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

class ViewStore {
  rootStore: RootStore;
  debugMode = false;

  // Layout state
  mobileOpen = false;
  isMobileView = false;
  miniActive = false;
  miniShow = false;
  darkMode = false;

  // PWA and service worker
  swRegistration: ServiceWorkerRegistration | undefined = undefined;
  subscription: PushSubscription | null = null;
  swUpdater: (() => void) | undefined;
  appUpdateFound = false;
  installPromptEvent: BeforeInstallPromptEvent | null = null;

  // Enhanced responsive properties
  windowHeight = window.innerHeight;
  windowWidth = window.innerWidth;
  orientation: OrientationType =
    screen.orientation?.type || "landscape-primary";
  pixelRatio = window.devicePixelRatio || 1;
  viewportHeight = window.visualViewport?.height || window.innerHeight;
  viewportWidth = window.visualViewport?.width || window.innerWidth;

  // Safe area support for modern devices (iOS notch, Android nav bars)
  safeAreaInsets = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };

  // Enhanced device detection
  isTouchEnabled = false;
  isTouchPrimary = false;
  isMousePrimary = false;
  isIphone = false;
  isIpad = false;
  isAndroid = false;
  isHybridDevice = false; // Devices that support both touch and mouse
  preferredInputMethod: "touch" | "mouse" | "hybrid" = "mouse";

  // Responsive sizes (will be calculated based on current breakpoint)
  headerHeight: number = defaultSizes.headerHeight;
  cardHeaderHeight: number = defaultSizes.cardHeaderHeight;
  tableHeaderHeight: number = defaultSizes.tableHeaderHeight;
  tableHeaderExtraHeight: number = defaultSizes.tableHeaderExtraHeight;
  tableWidthPadding: number = defaultSizes.tableWidthPadding;
  miniSidebarWidth: number = defaultSizes.miniSidebarWidth;
  fullSidebarWidth: number = defaultSizes.fullSidebarWidth;

  // Performance and accessibility
  reducedMotion = false;
  highContrast = false;
  forcedColors = false;

  // Resize handling optimization
  private resizeTimeout: NodeJS.Timeout | undefined;
  private orientationTimeout: NodeJS.Timeout | undefined;

  // Component debugging
  componentDomain = "";
  componentDomainDebug = false;

  // Cleanup functions
  getSubscriptionDispose: (() => void) | undefined;
  getServerSubscriptionDispose: (() => void) | undefined;
  updateNotificationDispose: (() => void) | undefined;
  sqRegUpdateDispose: (() => void) | undefined;
  checkForUpdateIntervalDispose: NodeJS.Timeout | undefined;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });

    this.rootStore = rootStore;

    reaction(
      () => this.isMobile,
      (isMobile) => {
        if (this.rootStore.aiAgentStore) {
          const aiAgentStore = this.rootStore.aiAgentStore as AIAgentStore;
          if (isMobile && aiAgentStore.scheduleMode) {
            aiAgentStore.toggleScheduleMode();
          }
        }
      },
      {
        fireImmediately: true,
      }
    );

    if (rootStore.viewStoreProps) {
      for (const [key, value] of Object.entries(rootStore.viewStoreProps)) {
        // Use indexed access with type assertion for dynamic properties
        (this as Record<string, unknown>)[key] = value;
      }
    }

    if (rootStore.appVersionStore) {
      //@ts-expect-error calling-service
      rootStore.appVersionStore.service.on(
        "created",
        (newAppVersion: { appName: string }) => {
          if (newAppVersion.appName === rootStore.appName) {
            if (this.swRegistration && this.swRegistration.update) {
              this.swRegistration.update(); // Tells the SW to check for app updates on any real navigation
            }
          }
        }
      );
    }
    if (rootStore.pushDeviceConnectionStore) {
      this.getSubscriptionDispose = when(
        () => !!this.swRegistration && !!this.swRegistration.pushManager,
        async () => {
          this.getSubscription();
        }
      );

      this.getServerSubscriptionDispose = when(
        () => !!rootStore.authStore?.isSignedIn,
        async () => {
          this.getServerSubscriptions();
        }
      );
    }

    if (rootStore.notificationStore) {
      this.updateNotificationDispose = reaction(
        () => this.appUpdateFound,
        (shouldUpdate) => {
          if (shouldUpdate) {
            if (rootStore.notificationStore) {
              rootStore.notificationStore.addStandardNotification("App Update");
            }
          }
        },
        { fireImmediately: true }
      );
    }

    if (rootStore.routerStore) {
      this.sqRegUpdateDispose = reaction(
        () => rootStore.routerStore?.currentRoute,
        (route) => {
          if (route && this.swRegistration && this.swRegistration.update) {
            this.swRegistration.update(); // Tells the SW to check for app updates on any real navigation
          }
        }
      );
    }
    this.updateDetectionValues();
    this.updateAccessibilityPreferences();
    this.updateSafeAreaInsets();

    const logStore = this.rootStore.logStore as ServiceStore;

    logStore?.create({
      level: "info",
      message: `ViewStore reporting enhanced device info`,
      payload: this.enhancedDeviceInfo,
      appName: "@intrasync/web",
    });

    if (window) {
      this.updateAppSizes();

      // Enhanced resize handling with debouncing
      window.addEventListener("resize", this.handleResize, { passive: true });

      // Orientation change handling
      if (screen.orientation) {
        screen.orientation.addEventListener(
          "change",
          this.handleOrientationChange
        );
      }

      // Visual viewport handling for mobile browsers (for virtual keyboard handling)
      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          "resize",
          this.handleViewportResize,
          { passive: true }
        );
      }

      // Media query listeners for accessibility preferences
      this.setupAccessibilityListeners();

      try {
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)"
        )?.matches;
        // User dark mode preference detected
        // this.setDarkMode(prefersDark);
      } catch (err) {
        console.log("Something went wrong checking preference");
      }
    }

    this.checkForUpdateIntervalDispose = setInterval(() => {
      return this.checkForAppUpdate();
    }, 1 * 60 * 60 * 1000); // Once every hour while in the app
  }

  updateDetectionValues = () => {
    this.isTouchEnabled =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error msMaxTouchPoints is not in the Navigator type
      navigator.msMaxTouchPoints > 0;
    this.isTouchPrimary = window.matchMedia("(pointer: coarse)").matches;
    this.isMousePrimary = window.matchMedia("(pointer: fine)").matches;
    this.isIphone = navigator.userAgent.toLowerCase().includes("iphone");
    this.isIpad =
      navigator.userAgent.toLowerCase().includes("ipad") ||
      (navigator.userAgent.toLowerCase().includes("macintosh") &&
        navigator.maxTouchPoints > 0);
    this.isAndroid = navigator.userAgent.toLowerCase().includes("android");

    // Enhanced device detection
    this.isHybridDevice = this.isTouchEnabled && this.isMousePrimary;
    this.preferredInputMethod = this.isTouchPrimary
      ? "touch"
      : this.isMousePrimary
      ? "mouse"
      : "hybrid";
    this.pixelRatio = window.devicePixelRatio || 1;

    if (this.debugMode)
      console.log("Enhanced Detection Values:", {
        isTouchEnabled: this.isTouchEnabled,
        isTouchPrimary: this.isTouchPrimary,
        isMousePrimary: this.isMousePrimary,
        isHybridDevice: this.isHybridDevice,
        preferredInputMethod: this.preferredInputMethod,
        isIphone: this.isIphone,
        isIpad: this.isIpad,
        isAndroid: this.isAndroid,
        pixelRatio: this.pixelRatio,
      });
  };

  updateAccessibilityPreferences = () => {
    try {
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      this.highContrast = window.matchMedia("(prefers-contrast: high)").matches;
      this.forcedColors = window.matchMedia("(forced-colors: active)").matches;
    } catch (err) {
      console.warn("Could not detect accessibility preferences:", err);
    }
  };

  updateSafeAreaInsets = () => {
    try {
      // Get CSS environment variables for safe areas
      const computedStyle = getComputedStyle(document.documentElement);
      this.safeAreaInsets = {
        top: parseInt(
          computedStyle.getPropertyValue("--safe-area-inset-top") || "0",
          10
        ),
        right: parseInt(
          computedStyle.getPropertyValue("--safe-area-inset-right") || "0",
          10
        ),
        bottom: parseInt(
          computedStyle.getPropertyValue("--safe-area-inset-bottom") || "0",
          10
        ),
        left: parseInt(
          computedStyle.getPropertyValue("--safe-area-inset-left") || "0",
          10
        ),
      };
    } catch (err) {
      // Fallback to detecting safe areas via viewport
      if (this.isIphone && window.screen && window.screen.height > 800) {
        // iPhone X and newer likely have a notch
        this.safeAreaInsets.top = 44;
        this.safeAreaInsets.bottom = 34;
      }
    }
  };

  setupAccessibilityListeners = () => {
    try {
      const reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      const highContrastQuery = window.matchMedia("(prefers-contrast: high)");
      const forcedColorsQuery = window.matchMedia("(forced-colors: active)");

      const updateAccessibility = () => {
        runInAction(() => {
          this.updateAccessibilityPreferences();
        });
      };

      reducedMotionQuery.addEventListener("change", updateAccessibility);
      highContrastQuery.addEventListener("change", updateAccessibility);
      forcedColorsQuery.addEventListener("change", updateAccessibility);
    } catch (err) {
      console.warn("Could not setup accessibility listeners:", err);
    }
  };

  handleResize = () => {
    // Debounce resize events for performance
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      runInAction(() => {
        this.updateAppSizes();
        this.updateDetectionValues();
        this.updateSafeAreaInsets();
      });
    }, 150); // 150ms debounce
  };

  handleOrientationChange = () => {
    // Debounce orientation changes as they can fire multiple times
    if (this.orientationTimeout) {
      clearTimeout(this.orientationTimeout);
    }

    this.orientationTimeout = setTimeout(() => {
      runInAction(() => {
        this.orientation = screen.orientation?.type || "landscape-primary";
        this.updateAppSizes();
        this.updateSafeAreaInsets();
      });
    }, 300); // Longer debounce for orientation as it's more expensive
  };

  handleViewportResize = () => {
    runInAction(() => {
      this.viewportHeight = window.visualViewport?.height || window.innerHeight;
      this.viewportWidth = window.visualViewport?.width || window.innerWidth;
    });
  };

  // Helper method to get responsive value based on current breakpoint
  getResponsiveValue = <T>(values: Record<BreakpointKey, T>): T => {
    const breakpoint = this.currentBreakpoint;
    return values[breakpoint];
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  setSWUpdater = (swUpdater: (() => void) | undefined) => {
    this.swUpdater = swUpdater;
  };

  setDarkMode = (value: boolean) => {
    this.darkMode = value;
  };

  /**
   * Sets component debug configuration
   * @param params - Debug configuration parameters
   */
  setComponentDebug = ({
    nextComponentDomain,
    shouldTrace = false,
  }: SetComponentDebugParams): void => {
    if (nextComponentDomain !== "" && !nextComponentDomain)
      throw new Error("Requires a component domain");
    this.componentDomain = nextComponentDomain;
    if (shouldTrace !== this.componentDomainDebug) {
      this.componentDomainDebug = shouldTrace;
    }
  };

  dispose = () => {
    // Cleanup existing disposers
    this.sqRegUpdateDispose && this.sqRegUpdateDispose();
    this.getSubscriptionDispose && this.getSubscriptionDispose();
    this.getServerSubscriptionDispose && this.getServerSubscriptionDispose();
    this.updateNotificationDispose && this.updateNotificationDispose();
    this.checkForUpdateIntervalDispose &&
      clearInterval(this.checkForUpdateIntervalDispose);

    // Cleanup new event listeners and timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.orientationTimeout) {
      clearTimeout(this.orientationTimeout);
    }

    // Remove event listeners
    if (window) {
      window.removeEventListener("resize", this.handleResize);

      if (screen.orientation) {
        screen.orientation.removeEventListener(
          "change",
          this.handleOrientationChange
        );
      }

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          this.handleViewportResize
        );
      }
    }
  };

  handleDuoWebAuth = async ({
    email,
    popupWindow = false,
  }: {
    email: string;
    popupWindow?: boolean;
  }) => {
    this.debugMode &&
      console.warn(
        "Debug handleDuoWebAuth email, popupWindow",
        email,
        popupWindow
      );
    if (!email) return new Error("Requires email");
    if (!window || !window.location) return new Error("Requires window");
    try {
      const createdRedirect =
        await this.rootStore.authStore?.createDuoAuthRedirect({ email });
      this.debugMode &&
        console.warn("Debug handleDuoWebAuth createdRedirect", createdRedirect);
      if (!createdRedirect)
        throw new Error(
          "Oops, something went wrong with createDuoAuthRedirect"
        );
      runInAction(() => {
        if (
          this.rootStore.appName === "@intrasync/plugin-manager" ||
          popupWindow
        ) {
          this.debugMode &&
            console.warn(
              "Debug handleDuoWebAuth @intrasync/plugin-manager popupWindow duoRedirectLink",
              this.rootStore.authStore?.duoRedirectLink
            );
          return window.open(
            this.rootStore.authStore?.duoRedirectLink,
            "IntraSync Duo Authentication",
            "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=400,height=520"
          );
        }
        this.debugMode &&
          console.warn(
            "Debug handleDuoWebAuth duoRedirectLink",
            this.rootStore.authStore?.duoRedirectLink
          );
        if (this.rootStore.authStore?.duoRedirectLink) {
          return window.location.assign(
            this.rootStore.authStore.duoRedirectLink
          );
        } else {
          throw new Error("Duo redirect link is undefined");
        }
      });
    } catch (err) {
      console.warn("Something went wrong with handleDuoWebAuth", err);
    }
  };

  openAutodeskAuthWindow = (email: string) => {
    this.debugMode && console.warn("Debug openAutodeskAuthWindow email", email);
    const userEmail = email || this.rootStore.authStore?.currentUser.email;
    if (!userEmail) {
      console.error(`Requires the user's email or an authenticated user`);
      return;
    }
    const autodeskAuthRedirectLink = {
      dev: `http://localhost:3372/auth/autodesk/request-token?email=${userEmail}`,
      development: `http://localhost:3372/auth/autodesk/request-token?email=${userEmail}`,
      stage: `https://stage.millerprecast-api.intrasync.app/auth/autodesk/request-token?email=${userEmail}`,
      beta: `https://stage.millerprecast-api.intrasync.app/auth/autodesk/request-token?email=${userEmail}`,
      prod: `https://millerprecast-api.intrasync.app/auth/autodesk/request-token?email=${userEmail}`,
      production: `https://millerprecast-api.intrasync.app/auth/autodesk/request-token?email=${userEmail}`,
    };

    this.debugMode &&
      console.warn(
        "Debug openAutodeskAuthWindow autodeskAuthRedirectLink",
        autodeskAuthRedirectLink[
          this.rootStore.mode as keyof typeof autodeskAuthRedirectLink
        ]
      );
    const mode = this.rootStore.mode as keyof typeof autodeskAuthRedirectLink;
    window.open(autodeskAuthRedirectLink[mode]);
  };

  setAppUpdateFound = (value: boolean) => {
    this.debugMode && console.warn("Debug setAppUpdateFound value", value);
    this.appUpdateFound = value;
  };

  getInstallPromptLastSeenAt = () => localStorage.getItem("pwaInstallPrompt");

  setInstallPromptSeenToday = () =>
    localStorage.setItem("pwaInstallPrompt", databaseDate());

  shouldBePromptedToInstall = () => {
    const daysToWaitBeforePromptingAgain = 30;
    const lastPromptDate = this.getInstallPromptLastSeenAt();
    const daysSinceLastPrompt = getDaysDiff(
      databaseDate(),
      lastPromptDate ?? ""
    );
    let displayMode = "browser tab";
    if ((navigator as Navigator & { standalone?: boolean }).standalone) {
      displayMode = "standalone-ios";
    }
    if (window.matchMedia("(display-mode: standalone)").matches) {
      displayMode = "standalone";
    }
    if (displayMode !== "browser tab") return false;
    return (
      isNaN(daysSinceLastPrompt) ||
      daysSinceLastPrompt > daysToWaitBeforePromptingAgain
    );
  };

  handleInstallAccepted = (key: string) => {
    if (key) {
      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.removeNotification(key);
      }
    }
    if (!this.installPromptEvent) {
      // The deferred prompt isn't available.
      return;
    }

    this.installPromptEvent.prompt();

    // decide what to do after the user chooses
    this.installPromptEvent.userChoice.then((choice: { outcome: string }) => {
      // if the user declined, we don't want to show the prompt again
      if (choice.outcome !== "accepted") {
        this.setInstallPromptSeenToday();
      }
      this.setInstallPromptEvent(null);
    });
  };

  setInstallPromptEvent = (event: null) => {
    this.installPromptEvent = event;
  };

  checkForAppUpdate = async () => {
    this.debugMode && console.warn("Debug checkForAppUpdate");
    if (this.swRegistration?.update) {
      this.debugMode &&
        console.warn("Debug checkForAppUpdate swRegistration.update()");
      return this.swRegistration.update();
    }
    // if (!this.rootStore.appVersionStore)
    //   return Error('Requires appVersionStore');
    // try {
    //   const appVersionStore = this.rootStore.appVersionStore as ServiceStore;

    //   const response = await appVersionStore.find({
    //     query: {
    //       required: true,
    //       appName: this.appName,
    //       $sort: {
    //         version: -1,
    //       },
    //       $limit: 1,
    //     },
    //   });
    //   this.debugMode &&
    //     console.warn(
    //       'Debug checkForAppUpdate appVersionStore.find response',
    //       response
    //     );
    //   if (response && response.data && response.data.length) {
    //     const latestVersion = response.data[0].version as string;

    //     const currentVersion = this.appVersion as string;
    //     this.debugMode &&
    //       console.warn(
    //         'Debug checkForAppUpdate currentVersion',
    //         currentVersion
    //       );
    //     this.debugMode &&
    //       console.warn('Debug checkForAppUpdate latestVersion', latestVersion);
    //     const shouldRefresh = versionCheck(latestVersion, currentVersion);

    //     this.debugMode &&
    //       console.warn('Debug checkForAppUpdate shouldRefresh', shouldRefresh);
    //     if (shouldRefresh) {
    //       this.setAppUpdateFound(true);
    //       if (this.rootStore.autoUpdate && this.rootStore.notificationStore) {
    //         await this.rootStore.notificationStore.addStandardNotification(
    //           'App Update'
    //         );
    //       } else return shouldRefresh;
    //     }
    //   }
    //   return false;
    // } catch (err) {
    //   console.warn('checkForAppUpdate ERROR', err);
    // }
  };
  setMobileOpen = (value: boolean) => {
    this.debugMode && console.warn("Debug setMobileOpen value", value);
    this.mobileOpen = value;
  };
  setMobileView = (value: boolean) => {
    this.debugMode && console.warn("Debug setMobileView value", value);
    if (value && this.miniActive) {
      this.debugMode && console.warn("Debug setMiniActive", false);
      this.setMiniActive(false);
    }
    this.isMobileView = value;
  };
  setMiniActive = async (value: boolean) => {
    this.debugMode && console.warn("Debug setMiniActive value", value);
    this.miniActive = value;
    await utils.sleep(300);
    this.updateAppSizes();
  };
  setMiniShow = async (value: boolean) => {
    this.debugMode && console.warn("Debug setMiniShow value", value);
    this.miniShow = value;
    await utils.sleep(400);
    this.updateAppSizes();
  };

  getServerSubscriptions = async () => {
    this.debugMode && console.warn("Debug getServerSubscriptions called");
    const pushDeviceConnectionStore = this.rootStore
      .pushDeviceConnectionStore as ServiceStore;
    await pushDeviceConnectionStore.find().catch((err) => {
      console.log("Error getting push device connections", err as Error);
    });
  };

  setSwRegistration = (reg: ServiceWorkerRegistration | undefined) => {
    this.debugMode && console.warn("Debug setSwRegistration reg", reg);
    this.swRegistration = reg;
  };

  reloadApp = async () => {
    if (this.swUpdater) {
      return this.swUpdater();
    } else {
      this.debugMode && console.warn("Debug reloadApp called");
      if (this.swRegistration) {
        this.debugMode && console.warn("Debug reloadApp using swRegistration");
        const registrationWaiting = this.swRegistration.waiting;
        this.debugMode &&
          console.warn(
            "Debug reloadApp registrationWaiting",
            registrationWaiting
          );
        if (registrationWaiting) {
          this.debugMode &&
            console.warn("Debug reloadApp registrationWaiting SKIP_WAITING");
          registrationWaiting.postMessage({ type: "SKIP_WAITING" });
          registrationWaiting.addEventListener("statechange", (e: Event) => {
            const target = e.target as ServiceWorker;
            if (target.state === "activated") {
              return window.location.reload();
            }
            if (e.target && (e.target as ServiceWorker).state === "activated") {
              return window.location.reload();
            }
          });
        }
      } else {
        this.debugMode && console.warn("Debug reloadApp clearBrowserCache");
        utils.clearBrowserCache();
        return window.location.reload();
      }
    }
  };

  getSubscription = async () => {
    this.debugMode && console.warn("Debug getSubscription called");
    if (!this.swRegistration) throw Error("Requires SW");
    const subscription =
      await this.swRegistration.pushManager.getSubscription();
    runInAction(() => {
      this.debugMode &&
        console.warn("Debug getSubscription subscription", subscription);
      this.subscription = subscription;
    });
  };

  subscribePushNotifications = async () => {
    this.debugMode && console.warn("Debug subscribePushNotifications called");
    if (!this.swRegistration) throw Error("Requires SW");

    const subscribeOptions = {
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    };
    this.debugMode &&
      console.warn(
        "Debug subscribePushNotifications subscribeOptions",
        subscribeOptions
      );
    const subscription = await this.swRegistration.pushManager.subscribe(
      subscribeOptions
    );
    this.debugMode &&
      console.warn(
        "Debug subscribePushNotifications subscription",
        subscription
      );
    const parsedSubscription = JSON.parse(JSON.stringify(subscription));
    this.debugMode &&
      console.warn(
        "Debug subscribePushNotifications parsedSubscription",
        parsedSubscription
      );
    const pushDeviceConnectionStore = this.rootStore
      .pushDeviceConnectionStore as ServiceStore;
    const createdSub = await pushDeviceConnectionStore.create({
      endpoint: parsedSubscription.endpoint,
      authKey: parsedSubscription.keys.auth || null,
      p256dhKey: parsedSubscription.keys.p256dh || null,
      expirationTime: parsedSubscription.expirationTime || null,
      userId: this.currentSubscriptionUser?.userId ?? "",
      deviceId: this.currentSubscriptionUser?.deviceId ?? "",
      appName: this.appName,
    });

    runInAction(() => {
      this.debugMode &&
        console.warn("Debug subscribePushNotifications createdSub", createdSub);
      this.subscription = subscription;
      console.log("createdSub", createdSub);
    });
  };

  unsubscribePushNotifications = async () => {
    this.debugMode && console.warn("Debug unsubscribePushNotifications called");
    if (!this.subscription) return Error("Requires SW Push Subscription");

    this.debugMode &&
      console.warn(
        "Debug unsubscribePushNotifications pushManager.unsubscribe()"
      );
    if (this.swRegistration) {
      // @ts-expect-error pushManager
      this.swRegistration.pushManager.unsubscribe();
    }
    if (
      this.currentSubscriptionUser?.userId &&
      this.currentSubscriptionUser?.deviceId
    ) {
      this.debugMode &&
        console.warn(
          "Debug unsubscribePushNotifications pushDeviceConnectionStore remove",
          {
            query: {
              userId: this.currentSubscriptionUser.userId,
              deviceId: this.currentSubscriptionUser.deviceId,
            },
          }
        );
      const pushDeviceConnectionStore = this.rootStore
        .pushDeviceConnectionStore as ServiceStore;
      await pushDeviceConnectionStore.remove(null, {
        query: {
          userId: this.currentSubscriptionUser.userId,
          deviceId: this.currentSubscriptionUser.deviceId,
        },
      });
    }
  };

  updateAppSizes = () => {
    this.debugMode && console.warn("Debug updateAppSizes called");

    // Update window dimensions
    this.windowHeight = window.innerHeight;
    this.windowWidth = window.innerWidth;

    // Update viewport dimensions if available
    if (window.visualViewport) {
      this.viewportHeight = window.visualViewport.height;
      this.viewportWidth = window.visualViewport.width;
    } else {
      this.viewportHeight = this.windowHeight;
      this.viewportWidth = this.windowWidth;
    }

    // Update responsive sizes based on current breakpoint
    const breakpoint = this.currentBreakpoint;
    this.headerHeight = LAYOUT_CONSTANTS.header.height[breakpoint];
    this.tableHeaderHeight = LAYOUT_CONSTANTS.table.headerHeight[breakpoint];
    this.tableHeaderExtraHeight =
      LAYOUT_CONSTANTS.table.headerExtraHeight[breakpoint];
    this.tableWidthPadding = LAYOUT_CONSTANTS.table.padding[breakpoint];
    this.miniSidebarWidth = LAYOUT_CONSTANTS.sidebar.mini[breakpoint];
    this.fullSidebarWidth = LAYOUT_CONSTANTS.sidebar.full[breakpoint];

    this.debugMode &&
      console.warn("Debug updateAppSizes", {
        windowHeight: this.windowHeight,
        windowWidth: this.windowWidth,
        viewportHeight: this.viewportHeight,
        viewportWidth: this.viewportWidth,
        breakpoint,
        headerHeight: this.headerHeight,
        safeAreaInsets: this.safeAreaInsets,
      });

    // Enhanced mobile view detection using breakpoints
    const wasMobile = this.isMobileView;
    const isMobile = this.windowWidth < BREAKPOINTS.md;

    if (wasMobile !== isMobile) {
      this.debugMode &&
        console.warn("Debug updateAppSizes setMobileView", isMobile);
      this.setMobileView(isMobile);
    }
  };

  /**
   * Legacy grid item width calculation (preserved for backwards compatibility)
   */
  calculateGridItemWidth = ({
    xs = 1,
    sm = 1,
    md = 2,
    lg = 3,
    xl = 4,
    xxl = 6,
  }: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  }): number => {
    const currentSize = this.currentGridSize;
    const columns =
      currentSize === "xs"
        ? xs
        : currentSize === "sm"
        ? sm
        : currentSize === "md"
        ? md
        : currentSize === "lg"
        ? lg
        : currentSize === "xl"
        ? xl
        : xxl;

    return this.cardBodySize.width / columns;
  };

  get currentSubscriptionUser() {
    const authStore = this.rootStore.authStore;
    if (!authStore) return null;
    if (!authStore.currentUser || !authStore.deviceId) return null;
    return {
      userId: authStore.userId,
      deviceId: authStore.deviceId,
    };
  }

  get currentSubscriptionFromStore() {
    if (
      !this.rootStore.pushDeviceConnectionStore ||
      !this.currentSubscriptionUser
    )
      return null;
    const pushDeviceConnectionStore = this.rootStore
      .pushDeviceConnectionStore as ServiceStore;
    return pushDeviceConnectionStore.entities
      .slice()
      .find(
        (i) =>
          (i as { userId: string | null; deviceId: string }).userId ===
            String(this.currentSubscriptionUser?.userId) &&
          String(
            (i as { userId: string | null; deviceId: string }).deviceId
          ) === String(this.currentSubscriptionUser?.deviceId)
      );
  }

  get authRedirectPath() {
    const { authStore } = this.rootStore;
    if (!authStore || !authStore.deviceId) return null;
    const redirectAddress = authAddress(
      this.appName.replace("@intrasync/", "")
    );
    return `${redirectAddress[this.mode as keyof AuthAddress]}/${
      authStore.deviceId
    }`;
  }

  get sidebarWidth() {
    return this.miniActive ? this.miniSidebarWidth : this.fullSidebarWidth;
  }

  get inBeta() {
    if (!this.appVersion) return null;
    return this.appVersion.includes("beta");
  }

  get cardHeaderSize() {
    const breakpoint = this.currentBreakpoint;
    const height = LAYOUT_CONSTANTS.cardHeader.height[breakpoint];
    const horizontalPadding =
      LAYOUT_CONSTANTS.cardHeader.padding.horizontal[breakpoint];

    return {
      height,
      width: Math.max(0, this.windowWidth - horizontalPadding),
    };
  }

  get cardBodySize() {
    const breakpoint = this.currentBreakpoint;
    const headerHeight = LAYOUT_CONSTANTS.header.height[breakpoint];
    const cardHeaderHeight = LAYOUT_CONSTANTS.cardHeader.height[breakpoint];
    const verticalPadding =
      LAYOUT_CONSTANTS.cardBody.padding.vertical[breakpoint];
    const horizontalPadding =
      LAYOUT_CONSTANTS.cardBody.padding.horizontal[breakpoint];

    const availableHeight = Math.max(
      0,
      this.viewportHeight -
        headerHeight -
        cardHeaderHeight -
        verticalPadding -
        this.safeAreaInsets.top -
        this.safeAreaInsets.bottom
    );

    const availableWidth = Math.max(
      0,
      this.windowWidth -
        horizontalPadding -
        this.safeAreaInsets.left -
        this.safeAreaInsets.right
    );

    return {
      height: availableHeight,
      width: availableWidth,
    };
  }

  get viewPortSize() {
    const breakpoint = this.currentBreakpoint;
    const headerHeight = LAYOUT_CONSTANTS.header.height[breakpoint];
    const horizontalPadding =
      LAYOUT_CONSTANTS.cardBody.padding.horizontal[breakpoint];

    // Enhanced viewport calculation with safe areas
    const height = Math.max(
      0,
      this.viewportHeight -
        headerHeight -
        this.safeAreaInsets.top -
        this.safeAreaInsets.bottom
    );

    const width =
      this.currentBreakpoint === "xs" || this.currentBreakpoint === "sm"
        ? this.windowWidth -
          this.safeAreaInsets.left -
          this.safeAreaInsets.right
        : Math.max(
            0,
            this.windowWidth -
              horizontalPadding -
              this.safeAreaInsets.left -
              this.safeAreaInsets.right
          );

    return { height, width };
  }

  get viewSize() {
    return {
      height: this.windowHeight - this.headerHeight,
      width: this.isMobileView
        ? this.windowWidth
        : this.windowWidth - this.sidebarWidth,
    };
  }

  get tableSize() {
    return {
      height: this.viewSize.height - this.tableHeaderHeight,
      heightExtraToolbar: this.viewSize.height - this.tableHeaderExtraHeight,
      width: this.viewSize.width - this.tableWidthPadding,
    };
  }

  get dialogSize() {
    return {
      height: this.windowHeight * 0.7,
      width: this.windowWidth * 0.7,
    };
  }

  get isDev() {
    return this.rootStore.authStore?.currentUser?.department === "Developer";
  }

  get isWebView() {
    const { routerStore } = this.rootStore;
    // console.log('routerStore', routerStore);
    if (!routerStore || !routerStore.queryParams) return false;
    return routerStore.queryParams.webview;
  }

  get isTouchDevice() {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }

  get isIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) &&
        document &&
        "ontouchend" in document)
    );
  }

  get appVersion() {
    return this.rootStore.appVersion;
  }

  get appName() {
    return this.rootStore.appName;
  }

  get appTitle() {
    if (this.rootStore.appTitle) {
      return this.rootStore.appTitle;
    }
    return "IntraSync App";
  }

  get mode() {
    return this.rootStore.mode;
  }

  get isLoading() {
    return (
      Array.isArray(this.rootStore?.anyServiceStoreLoading) &&
      this.rootStore.anyServiceStoreLoading.length > 0
    );
  }

  get isConnected() {
    return this.rootStore.connectionStore?.serverConnectionStatus;
  }

  get isPhone() {
    return this.windowWidth <= 600; // ThemeProvider uses sm=600
  }

  get isTablet() {
    return this.windowWidth <= 960 && this.windowWidth > 600; // ThemeProvider uses md=960
  }

  get isMobile() {
    return this.isPhone || this.isTablet;
  }

  get isDesktop() {
    return this.windowWidth > 960;
  }

  // Enhanced breakpoint detection using BREAKPOINTS constant
  get currentBreakpoint(): BreakpointKey {
    const width = this.windowWidth;
    if (width >= BREAKPOINTS.xxl) return "xxl";
    if (width >= BREAKPOINTS.xl) return "xl";
    if (width >= BREAKPOINTS.lg) return "lg";
    if (width >= BREAKPOINTS.md) return "md";
    if (width >= BREAKPOINTS.sm) return "sm";
    return "xs";
  }

  // Legacy getter for backwards compatibility
  get currentGridSize() {
    const breakpoint = this.currentBreakpoint;
    // Map new breakpoints to legacy grid sizes
    if (breakpoint === "xxl") return "xl";
    return breakpoint;
  }

  // Enhanced device info with new properties
  get enhancedDeviceInfo() {
    return {
      // Basic device detection
      isTouchEnabled: this.isTouchEnabled,
      isTouchPrimary: this.isTouchPrimary,
      isMousePrimary: this.isMousePrimary,
      isHybridDevice: this.isHybridDevice,
      preferredInputMethod: this.preferredInputMethod,

      // Platform detection
      isIphone: this.isIphone,
      isIpad: this.isIpad,
      isAndroid: this.isAndroid,
      isIOS: this.isIOS,

      // Display properties
      pixelRatio: this.pixelRatio,
      orientation: this.orientation,
      currentBreakpoint: this.currentBreakpoint,

      // Accessibility
      reducedMotion: this.reducedMotion,
      highContrast: this.highContrast,
      forcedColors: this.forcedColors,

      // Safe areas
      safeAreaInsets: this.safeAreaInsets,

      // Viewport
      windowSize: { width: this.windowWidth, height: this.windowHeight },
      viewportSize: { width: this.viewportWidth, height: this.viewportHeight },
    };
  }

  // Legacy getter for backwards compatibility
  get deviceInfo() {
    return {
      isTouchEnabled: this.isTouchEnabled,
      isTouchPrimary: this.isTouchPrimary,
      isMousePrimary: this.isMousePrimary,
      isIphone: this.isIphone,
      isIpad: this.isIpad,
      isAndroid: this.isAndroid,
    };
  }

  // New responsive utilities
  get isPortrait() {
    return (
      this.orientation.includes("portrait") ||
      this.windowHeight > this.windowWidth
    );
  }

  get isLandscape() {
    return (
      this.orientation.includes("landscape") ||
      this.windowWidth > this.windowHeight
    );
  }

  get isUltraWide() {
    return this.currentBreakpoint === "xxl";
  }

  get hasNotch() {
    return this.safeAreaInsets.top > 20; // Reasonable threshold for notch detection
  }

  get effectiveViewportHeight() {
    // Account for virtual keyboards on mobile
    return this.viewportHeight;
  }

  get densityClass() {
    if (this.pixelRatio >= 3) return "high-density";
    if (this.pixelRatio >= 2) return "medium-density";
    return "standard-density";
  }

  // Enhanced responsive grid utilities
  getGridColumns = (config: Partial<Record<BreakpointKey, number>> = {}) => {
    const defaultConfig: Record<BreakpointKey, number> = {
      xs: 1,
      sm: 2,
      md: 3,
      lg: 4,
      xl: 5,
      xxl: 6,
    };

    const finalConfig = { ...defaultConfig, ...config };
    return finalConfig[this.currentBreakpoint];
  };

  // Enhanced grid item width calculation
  calculateResponsiveGridItemWidth = (
    config?: Partial<Record<BreakpointKey, number>>
  ) => {
    const columns = this.getGridColumns(config);
    return this.cardBodySize.width / columns;
  };
}
export default ViewStore;
